import { useState, useCallback, useEffect } from 'react';
import { Plus, Trash2, Edit } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { guideAPI } from '../../lib/api';
import { toast } from 'sonner';

const GuidesManagement = () => {
  const [guides, setGuides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingGuide, setEditingGuide] = useState(null);
  const [form, setForm] = useState({
    title: '', slug: '', category: 'documentation', summary: '',
    steps: [{ step_number: 1, title: '', description: '' }],
    images: [], pdfs: []
  });

  const fetchGuides = useCallback(async () => {
    try {
      const res = await guideAPI.getAll();
      setGuides(res.data);
    } catch (err) {
      console.error('Failed to fetch guides:', err);
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchGuides(); }, [fetchGuides]);

  const resetForm = () => {
    setForm({ title: '', slug: '', category: 'documentation', summary: '', steps: [{ step_number: 1, title: '', description: '' }], images: [], pdfs: [] });
    setEditingGuide(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingGuide) { await guideAPI.update(editingGuide.id, form); toast.success('গাইড আপডেট হয়েছে'); }
      else { await guideAPI.create(form); toast.success('গাইড তৈরি হয়েছে'); }
      fetchGuides(); setIsDialogOpen(false); resetForm();
    } catch (err) { toast.error('কিছু সমস্যা হয়েছে'); }
  };

  const handleEdit = (guide) => {
    setEditingGuide(guide);
    setForm({ title: guide.title, slug: guide.slug, category: guide.category, summary: guide.summary, steps: guide.steps, images: guide.images, pdfs: guide.pdfs });
    setIsDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('এই গাইড মুছে ফেলতে চান?')) {
      try { await guideAPI.delete(id); toast.success('গাইড মুছে ফেলা হয়েছে'); fetchGuides(); }
      catch (err) { toast.error('মুছতে সমস্যা হয়েছে'); }
    }
  };

  const addStep = () => {
    setForm({ ...form, steps: [...form.steps, { step_number: form.steps.length + 1, title: '', description: '' }] });
  };

  const updateStep = (index, field, value) => {
    const newSteps = [...form.steps];
    newSteps[index][field] = value;
    setForm({ ...form, steps: newSteps });
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <h1 className="text-xl md:text-2xl font-bold text-gray-900 dark:text-white">গাইড ম্যানেজমেন্ট</h1>
        <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="bg-[#2E7D32] hover:bg-[#1B5E20] w-full sm:w-auto"><Plus size={18} className="mr-2" /> নতুন গাইড</Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>{editingGuide ? 'গাইড সম্পাদনা' : 'নতুন গাইড তৈরি'}</DialogTitle></DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div><label className="block text-sm font-medium mb-1">শিরোনাম</label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required /></div>
                <div><label className="block text-sm font-medium mb-1">Slug</label><Input value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} required /></div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">ক্যাটাগরি</label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="documentation">ডকুমেন্টেশন</SelectItem>
                    <SelectItem value="legal">আইনি</SelectItem>
                    <SelectItem value="fees">ফি ও খরচ</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><label className="block text-sm font-medium mb-1">সারসংক্ষেপ</label><Textarea value={form.summary} onChange={(e) => setForm({ ...form, summary: e.target.value })} required /></div>
              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="text-sm font-medium">ধাপসমূহ</label>
                  <Button type="button" variant="outline" size="sm" onClick={addStep}><Plus size={14} className="mr-1" /> ধাপ যোগ</Button>
                </div>
                {form.steps.map((step, index) => (
                  <div key={`step-${step.step_number}`} className="border dark:border-gray-600 rounded-lg p-3 mb-2">
                    <Input placeholder="ধাপের শিরোনাম" value={step.title} onChange={(e) => updateStep(index, 'title', e.target.value)} className="mb-2" />
                    <Textarea placeholder="বিবরণ" value={step.description} onChange={(e) => updateStep(index, 'description', e.target.value)} rows={2} />
                  </div>
                ))}
              </div>
              <Button type="submit" className="w-full bg-[#2E7D32] hover:bg-[#1B5E20]">{editingGuide ? 'আপডেট করুন' : 'তৈরি করুন'}</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="animate-pulse space-y-4">{[1, 2, 3].map(i => <div key={i} className="h-20 bg-gray-200 dark:bg-gray-700 rounded-lg" />)}</div>
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-x-auto">
          <table className="w-full min-w-[500px]">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-300">শিরোনাম</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-300">ক্যাটাগরি</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-300">ধাপ</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-600 dark:text-gray-300">অ্যাকশন</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {guides.map((guide) => (
                <tr key={guide.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                  <td className="px-4 py-3">
                    <p className="font-medium text-gray-900 dark:text-white text-sm">{guide.title}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">{guide.slug}</p>
                  </td>
                  <td className="px-4 py-3"><Badge variant="secondary" className="text-xs">{guide.category}</Badge></td>
                  <td className="px-4 py-3 text-gray-600 dark:text-gray-300 text-sm">{guide.steps?.length || 0}</td>
                  <td className="px-4 py-3 text-right">
                    <Button variant="ghost" size="sm" onClick={() => handleEdit(guide)}><Edit size={16} /></Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDelete(guide.id)}><Trash2 size={16} className="text-red-500" /></Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default GuidesManagement;
